/**
 * Winwheel.js - Canvas wheel rendering library
 * Extracted and refactored from AhaSlides Spinner Wheel
 */

import { gsap } from 'gsap'

let winwheelToDrawDuringAnimation = null

/**
 * Animation configuration class
 */
export class Animation {
  constructor(options) {
    const defaults = {
      type: 'spinOngoing',
      direction: 'clockwise',
      propertyName: null,
      propertyValue: null,
      duration: 10,
      yoyo: false,
      repeat: null,
      easing: null,
      stopAngle: null,
      spins: null,
      clearTheCanvas: null,
      callbackFinished: null,
      callbackBefore: null,
      callbackAfter: null,
      callbackSound: null,
    }

    for (const key in defaults) {
      this[key] = options?.[key] ?? defaults[key]
    }

    // Copy any additional properties
    if (options) {
      for (const key in options) {
        if (this[key] === undefined) {
          this[key] = options[key]
        }
      }
    }
  }
}

/**
 * Segment configuration class
 */
export class Segment {
  constructor(options) {
    const defaults = {
      size: null,
      text: '',
      fillStyle: null,
      strokeStyle: null,
      lineWidth: null,
      textFontFamily: null,
      textFontSize: null,
      textFontWeight: null,
      textOrientation: null,
      textAlignment: null,
      textDirection: null,
      textMargin: null,
      textFillStyle: null,
      textStrokeStyle: null,
      textLineWidth: null,
    }

    for (const key in defaults) {
      this[key] = options?.[key] ?? defaults[key]
    }

    // Copy any additional properties
    if (options) {
      for (const key in options) {
        if (this[key] === undefined) {
          this[key] = options[key]
        }
      }
    }

    this.startAngle = 0
    this.endAngle = 0
  }
}

/**
 * Pointer guide configuration class
 */
export class PointerGuide {
  constructor(options) {
    const defaults = {
      display: false,
      strokeStyle: 'red',
      lineWidth: 3,
    }

    for (const key in defaults) {
      this[key] = options?.[key] ?? defaults[key]
    }
  }
}

/**
 * Trigger sound callback during animation
 */
function winwheelTriggerSound() {
  if (!winwheelToDrawDuringAnimation._lastSoundTriggerNumber) {
    winwheelToDrawDuringAnimation._lastSoundTriggerNumber = 0
  }

  const callbackSound = winwheelToDrawDuringAnimation.animation.callbackSound
  const currentTriggerNumber = winwheelToDrawDuringAnimation.getIndicatedSegmentNumber()

  if (currentTriggerNumber !== winwheelToDrawDuringAnimation._lastSoundTriggerNumber) {
    if (typeof callbackSound === 'function') {
      callbackSound()
    }
    winwheelToDrawDuringAnimation._lastSoundTriggerNumber = currentTriggerNumber
  }
}

/**
 * Animation loop function
 */
function winwheelAnimationLoop() {
  if (!winwheelToDrawDuringAnimation) return

  if (winwheelToDrawDuringAnimation.animation.clearTheCanvas !== false) {
    winwheelToDrawDuringAnimation.ctx.clearRect(
      0,
      0,
      winwheelToDrawDuringAnimation.canvas.width,
      winwheelToDrawDuringAnimation.canvas.height
    )
  }

  const callbackBefore = winwheelToDrawDuringAnimation.animation.callbackBefore
  const callbackAfter = winwheelToDrawDuringAnimation.animation.callbackAfter

  if (callbackBefore) {
    if (typeof callbackBefore === 'function') {
      callbackBefore(winwheelToDrawDuringAnimation.getIndicatedSegment())
    }
  }

  winwheelToDrawDuringAnimation.draw(false)

  if (callbackAfter) {
    if (typeof callbackAfter === 'function') {
      callbackAfter(winwheelToDrawDuringAnimation.getIndicatedSegment())
    }
  }

  if (winwheelToDrawDuringAnimation.animation.callbackSound) {
    winwheelTriggerSound()
  }
}

/**
 * Stop animation callback
 */
function winwheelStopAnimation(canCallback) {
  if (canCallback !== false) {
    const callback = winwheelToDrawDuringAnimation.animation.callbackFinished
    if (callback) {
      if (typeof callback === 'function') {
        callback(winwheelToDrawDuringAnimation.getIndicatedSegment())
      }
    }
  }
}

/**
 * Handle window resize for responsive wheels
 */
function winwheelResize() {
  let margin = 40
  if (winwheelToDrawDuringAnimation._responsiveMargin !== undefined) {
    margin = winwheelToDrawDuringAnimation._responsiveMargin
  }

  let newWidth = window.innerWidth - margin
  const minWidth = winwheelToDrawDuringAnimation._responsiveMinWidth
  const minHeight = winwheelToDrawDuringAnimation._responsiveMinHeight

  if (newWidth < minWidth) {
    newWidth = minWidth
  } else if (newWidth > winwheelToDrawDuringAnimation._originalCanvasWidth) {
    newWidth = winwheelToDrawDuringAnimation._originalCanvasWidth
  }

  const scaleFactor = newWidth / winwheelToDrawDuringAnimation._originalCanvasWidth

  winwheelToDrawDuringAnimation.canvas.width =
    winwheelToDrawDuringAnimation._originalCanvasWidth * scaleFactor

  if (winwheelToDrawDuringAnimation._responsiveScaleHeight) {
    let newHeight = winwheelToDrawDuringAnimation._originalCanvasHeight * scaleFactor
    if (newHeight < minHeight) {
      newHeight = minHeight
    } else if (newHeight > winwheelToDrawDuringAnimation._originalCanvasHeight) {
      newHeight = winwheelToDrawDuringAnimation._originalCanvasHeight
    }
    winwheelToDrawDuringAnimation.canvas.height = newHeight
  }

  winwheelToDrawDuringAnimation.scaleFactor = scaleFactor
  winwheelToDrawDuringAnimation.invalidateFaceCache()
  winwheelToDrawDuringAnimation.draw()
}

/**
 * Main Winwheel class for creating and animating spinning wheels
 */
export class Winwheel {
  constructor(options, drawWheel = true) {
    const defaults = {
      canvasId: 'canvas',
      centerX: null,
      centerY: null,
      outerRadius: null,
      innerRadius: 0,
      numSegments: 1,
      drawMode: 'code',
      rotationAngle: 0,
      textFontFamily: 'Arial',
      textFontSize: 20,
      textFontWeight: 'bold',
      textOrientation: 'horizontal',
      textAlignment: 'center',
      textDirection: 'normal',
      textMargin: null,
      textFillStyle: 'black',
      textStrokeStyle: null,
      textLineWidth: 1,
      fillStyle: 'silver',
      strokeStyle: 'white',
      lineWidth: 1,
      clearTheCanvas: true,
      drawText: true,
      pointerAngle: 0,
      responsive: false,
      scaleFactor: 1,
    }

    // Apply defaults and options
    for (const key in defaults) {
      this[key] = options?.[key] ?? defaults[key]
    }

    // Copy any additional properties
    if (options) {
      for (const key in options) {
        if (this[key] === undefined) {
          this[key] = options[key]
        }
      }
    }

    // Setup canvas
    if (this.canvasId) {
      this.canvas = document.getElementById(this.canvasId)
      if (this.canvas) {
        if (this.centerX === null) {
          this.centerX = this.canvas.width / 2
        }
        if (this.centerY === null) {
          this.centerY = this.canvas.height / 2
        }
        if (this.outerRadius === null) {
          this.outerRadius =
            this.canvas.width < this.canvas.height
              ? this.canvas.width / 2 - this.lineWidth
              : this.canvas.height / 2 - this.lineWidth
        }
        this.ctx = this.canvas.getContext('2d')
      } else {
        this.canvas = null
        this.ctx = null
      }
    } else {
      this.canvas = null
      this.ctx = null
    }

    // Initialize segments array
    this.segments = [null]
    for (let i = 1; i <= this.numSegments; i++) {
      if (options?.segments?.[i - 1]) {
        this.segments[i] = new Segment(options.segments[i - 1])
      } else {
        this.segments[i] = new Segment()
      }
    }

    this.updateSegmentSizes()

    // Set text margin
    if (this.textMargin === null) {
      this.textMargin = this.textFontSize / 1.7
    }

    // Setup animation
    if (options?.animation) {
      this.animation = new Animation(options.animation)
    } else {
      this.animation = new Animation()
    }

    // Setup pointer guide
    if (options?.pointerGuide) {
      this.pointerGuide = new PointerGuide(options.pointerGuide)
    } else {
      this.pointerGuide = new PointerGuide()
    }

    // Setup responsive behavior
    if (this.responsive) {
      winwheelToDrawDuringAnimation = this
      this._originalCanvasWidth = this.canvas.width
      this._originalCanvasHeight = this.canvas.height
      this._responsiveScaleHeight = this.canvas.dataset.responsivescaleheight
      this._responsiveMinWidth = this.canvas.dataset.responsiveminwidth
      this._responsiveMinHeight = this.canvas.dataset.responsiveminheight
      this._responsiveMargin = this.canvas.dataset.responsivemargin
      window.addEventListener('load', winwheelResize)
      window.addEventListener('resize', winwheelResize)
    }

    // Initial draw
    if (drawWheel) {
      this.draw(this.clearTheCanvas)
    }
  }

  /**
   * Update segment sizes based on configuration
   */
  updateSegmentSizes() {
    if (!this.segments) return

    let fixedTotal = 0
    let fixedCount = 0

    for (let i = 1; i <= this.numSegments; i++) {
      if (this.segments[i].size !== null) {
        fixedTotal += this.segments[i].size
        fixedCount++
      }
    }

    const remaining = 360 - fixedTotal
    const autoSize = remaining > 0 ? remaining / (this.numSegments - fixedCount) : 0

    let currentAngle = 0
    for (let i = 1; i <= this.numSegments; i++) {
      this.segments[i].startAngle = currentAngle
      currentAngle += this.segments[i].size ?? autoSize
      this.segments[i].endAngle = currentAngle
    }

    this._faceCacheValid = false
  }

  invalidateFaceCache() {
    this._faceCacheValid = false
    this._screenFaceValid = false
  }

  _ensureFaceCache() {
    if (!this.canvas) return

    const w = this.canvas.width
    const h = this.canvas.height

    if (!this._faceCache) {
      this._faceCache = document.createElement('canvas')
      this._faceCacheValid = false
    }
    if (this._faceCache.width !== w || this._faceCache.height !== h) {
      this._faceCache.width = w
      this._faceCache.height = h
      this._faceCacheValid = false
    }

    if (this._faceCacheValid) return

    const cacheCtx = this._faceCache.getContext('2d')
    cacheCtx.clearRect(0, 0, w, h)

    const realCtx = this.ctx
    const realRotation = this.rotationAngle
    this.ctx = cacheCtx
    this.rotationAngle = 0

    this.drawSegments()
    if (this.drawText) {
      this.drawSegmentText()
    }

    this.ctx = realCtx
    this.rotationAngle = realRotation
    this._faceCacheValid = true
    // Offscreen content just changed — the on-screen blit is now stale.
    this._screenFaceValid = false
  }

  /**
   * Clear the canvas
   */
  clearCanvas() {
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    }
    this._screenFaceValid = false
  }

  _getRotationElement() {
    const parent = this.canvas?.parentElement
    return parent?.dataset?.wheelRotator === 'true' ? parent : null
  }

  _getFaceImageElement() {
    return this._getRotationElement()?.querySelector('[data-wheel-face="true"]') ?? null
  }

  _syncFaceImage() {
    const faceImage = this._getFaceImageElement()
    if (!faceImage || !this._faceCache) return

    faceImage.src = this._faceCache.toDataURL('image/png')
  }

  _applyCssRotation() {
    if (!this.canvas) return

    const rotation = this.rotationAngle % 360
    const rotator = this._getRotationElement()

    if (rotator) {
      const transform = `translate3d(-50%, -50%, 0) rotate(${rotation}deg)`
      rotator.style.transform = transform
      rotator.style.webkitTransform = transform
      return
    }

    // Backward-compatible fallback for consumers that still rotate the canvas
    // with a stylesheet custom property.
    this.canvas.style.setProperty('--wheel-rotation', `${rotation}deg`)
  }

  /**
   * Draw the wheel.
   *
   * Strategy: render segments + text to an offscreen face canvas once, blit
   * that face to the on-screen canvas once (at rotation 0), mirror it into a
   * static image layer, then on every tick just update a compositor-owned
   * wrapper transform to rotate it.
   *
   * This keeps the animated layer as a regular image instead of an actively
   * composited canvas, which avoids Safari/WebKit canvas texture churn while
   * preserving the canvas fallback and drawing API.
   */
  draw(clearCanvas) {
    if (!this.ctx) return

    if (clearCanvas === undefined || clearCanvas === true) {
      this.clearCanvas()
    }

    this._ensureFaceCache()

    if (this._faceCache && this.canvas && !this.pointerGuide.display) {
      if (!this._screenFaceValid) {
        // Blit the face to the on-screen canvas once at rotation 0.
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.ctx.drawImage(this._faceCache, 0, 0)
        this._syncFaceImage()
        this._screenFaceValid = true
      }
      this._applyCssRotation()
    } else {
      // Fallback path: no face cache, or pointer-guide debug overlay is on.
      this.ctx.save()
      const centerX = this.centerX * this.scaleFactor
      const centerY = this.centerY * this.scaleFactor
      this.ctx.translate(centerX, centerY)
      this.ctx.rotate(this.degToRad(this.rotationAngle))
      this.ctx.translate(-centerX, -centerY)
      if (this._faceCache) {
        this.ctx.drawImage(this._faceCache, 0, 0)
      } else {
        this.drawSegments()
        if (this.drawText) {
          this.drawSegmentText()
        }
      }
      this.ctx.restore()

      if (this.pointerGuide.display) {
        this.drawPointerGuide()
      }
    }
  }

  /**
   * Draw pointer guide for debugging
   */
  drawPointerGuide() {
    if (!this.ctx) return

    const centerX = this.centerX * this.scaleFactor
    const centerY = this.centerY * this.scaleFactor
    const outerRadius = this.outerRadius * this.scaleFactor

    this.ctx.save()
    this.ctx.translate(centerX, centerY)
    this.ctx.rotate(this.degToRad(this.pointerAngle))
    this.ctx.translate(-centerX, -centerY)

    this.ctx.strokeStyle = this.pointerGuide.strokeStyle
    this.ctx.lineWidth = this.pointerGuide.lineWidth
    this.ctx.beginPath()
    this.ctx.moveTo(centerX, centerY)
    this.ctx.lineTo(centerX, -outerRadius / 4)
    this.ctx.stroke()

    this.ctx.restore()
  }

  /**
   * Draw all segments
   */
  drawSegments() {
    if (!this.ctx || !this.segments) return

    const centerX = this.centerX * this.scaleFactor
    const centerY = this.centerY * this.scaleFactor
    const innerRadius = this.innerRadius * this.scaleFactor
    const outerRadius = this.outerRadius * this.scaleFactor

    for (let i = 1; i <= this.numSegments; i++) {
      const segment = this.segments[i]

      const fillStyle = segment.fillStyle ?? this.fillStyle
      const lineWidth = segment.lineWidth ?? this.lineWidth
      const strokeStyle = segment.strokeStyle ?? this.strokeStyle

      this.ctx.fillStyle = fillStyle
      this.ctx.lineWidth = lineWidth
      this.ctx.strokeStyle = strokeStyle

      if (!strokeStyle && !fillStyle) continue

      this.ctx.beginPath()

      if (this.innerRadius) {
        const startX =
          Math.cos(this.degToRad(segment.startAngle + this.rotationAngle - 90)) *
          (innerRadius - lineWidth / 2)
        const startY =
          Math.sin(this.degToRad(segment.startAngle + this.rotationAngle - 90)) *
          (innerRadius - lineWidth / 2)
        this.ctx.moveTo(centerX + startX, centerY + startY)
      } else {
        this.ctx.moveTo(centerX, centerY)
      }

      this.ctx.arc(
        centerX,
        centerY,
        outerRadius,
        this.degToRad(segment.startAngle + this.rotationAngle - 90),
        this.degToRad(segment.endAngle + this.rotationAngle - 90),
        false
      )

      if (this.innerRadius) {
        this.ctx.arc(
          centerX,
          centerY,
          innerRadius,
          this.degToRad(segment.endAngle + this.rotationAngle - 90),
          this.degToRad(segment.startAngle + this.rotationAngle - 90),
          true
        )
      } else {
        this.ctx.lineTo(centerX, centerY)
      }

      if (fillStyle) {
        this.ctx.fill()
      }
      if (strokeStyle) {
        this.ctx.stroke()
      }
    }
  }

  /**
   * Draw text on segments
   */
  drawSegmentText() {
    if (!this.ctx) return

    const centerX = this.centerX * this.scaleFactor
    const centerY = this.centerY * this.scaleFactor
    const outerRadius = this.outerRadius * this.scaleFactor
    const innerRadius = this.innerRadius * this.scaleFactor

    for (let i = 1; i <= this.numSegments; i++) {
      this.ctx.save()

      const segment = this.segments[i]
      if (!segment.text) {
        this.ctx.restore()
        continue
      }

      const fontFamily = segment.textFontFamily ?? this.textFontFamily
      let fontSize = segment.textFontSize ?? this.textFontSize
      const fontWeight = segment.textFontWeight ?? this.textFontWeight
      let orientation = segment.textOrientation ?? this.textOrientation
      const alignment = segment.textAlignment ?? this.textAlignment
      const direction = segment.textDirection ?? this.textDirection
      let margin = segment.textMargin ?? this.textMargin

      // Curved text save/rotate/restore per character is the worst path on iOS.
      // Fall back to horizontal when there are too many segments to keep up.
      if (orientation === 'curved' && this.numSegments > Winwheel.MAX_CURVED_TEXT_SEGMENTS) {
        orientation = 'horizontal'
      }
      const fillStyle = segment.textFillStyle ?? this.textFillStyle
      const strokeStyle = segment.textStrokeStyle ?? this.textStrokeStyle
      const lineWidth = segment.textLineWidth ?? this.textLineWidth

      fontSize *= this.scaleFactor
      margin *= this.scaleFactor

      // Build font string
      let fontString = ''
      if (fontWeight) fontString += `${fontWeight} `
      if (fontSize) fontString += `${fontSize}px `
      if (fontFamily) fontString += fontFamily

      this.ctx.font = fontString
      this.ctx.fillStyle = fillStyle
      this.ctx.strokeStyle = strokeStyle
      this.ctx.lineWidth = lineWidth

      // Split text into lines
      const lines = segment.text.split('\n')
      let lineOffset = -fontSize * (lines.length / 2) + fontSize / 2

      if (orientation === 'curved' && (alignment === 'inner' || alignment === 'outer')) {
        lineOffset = 0
      }

      for (let j = 0; j < lines.length; j++) {
        if (direction === 'reversed') {
          this._drawReversedText(
            lines[j],
            segment,
            orientation,
            alignment,
            centerX,
            centerY,
            outerRadius,
            innerRadius,
            fontSize,
            margin,
            fillStyle,
            strokeStyle,
            lineOffset
          )
        } else {
          this._drawNormalText(
            lines[j],
            segment,
            orientation,
            alignment,
            centerX,
            centerY,
            outerRadius,
            innerRadius,
            fontSize,
            margin,
            fillStyle,
            strokeStyle,
            lineOffset
          )
        }
        lineOffset += fontSize
      }

      this.ctx.restore()
    }
  }

  /**
   * Draw text in normal direction
   */
  _drawNormalText(
    text,
    segment,
    orientation,
    alignment,
    centerX,
    centerY,
    outerRadius,
    innerRadius,
    fontSize,
    margin,
    fillStyle,
    strokeStyle,
    lineOffset
  ) {
    if (orientation === 'horizontal') {
      this.ctx.textAlign = alignment === 'inner' ? 'left' : alignment === 'outer' ? 'right' : 'center'
      this.ctx.textBaseline = 'middle'

      const angle = this.degToRad(
        segment.endAngle - (segment.endAngle - segment.startAngle) / 2 + this.rotationAngle - 90
      )

      this.ctx.save()
      this.ctx.translate(centerX, centerY)
      this.ctx.rotate(angle)
      this.ctx.translate(-centerX, -centerY)

      let x
      if (alignment === 'inner') {
        x = centerX + innerRadius + margin
      } else if (alignment === 'outer') {
        x = centerX + outerRadius - margin
      } else {
        x = centerX + innerRadius + (outerRadius - innerRadius) / 2 + margin
      }

      if (fillStyle) this.ctx.fillText(text, x, centerY + lineOffset)
      if (strokeStyle) this.ctx.strokeText(text, x, centerY + lineOffset)

      this.ctx.restore()
    } else if (orientation === 'vertical') {
      this._drawVerticalText(
        text,
        segment,
        alignment,
        centerX,
        centerY,
        outerRadius,
        innerRadius,
        fontSize,
        margin,
        fillStyle,
        strokeStyle,
        lineOffset,
        false
      )
    } else if (orientation === 'curved') {
      this._drawCurvedText(
        text,
        segment,
        alignment,
        centerX,
        centerY,
        outerRadius,
        innerRadius,
        fontSize,
        margin,
        fillStyle,
        strokeStyle,
        lineOffset,
        false
      )
    }
  }

  /**
   * Draw text in reversed direction
   */
  _drawReversedText(
    text,
    segment,
    orientation,
    alignment,
    centerX,
    centerY,
    outerRadius,
    innerRadius,
    fontSize,
    margin,
    fillStyle,
    strokeStyle,
    lineOffset
  ) {
    if (orientation === 'horizontal') {
      this.ctx.textAlign = alignment === 'inner' ? 'right' : alignment === 'outer' ? 'left' : 'center'
      this.ctx.textBaseline = 'middle'

      const angle = this.degToRad(
        segment.endAngle - (segment.endAngle - segment.startAngle) / 2 + this.rotationAngle - 90 - 180
      )

      this.ctx.save()
      this.ctx.translate(centerX, centerY)
      this.ctx.rotate(angle)
      this.ctx.translate(-centerX, -centerY)

      let x
      if (alignment === 'inner') {
        x = centerX - innerRadius - margin
      } else if (alignment === 'outer') {
        x = centerX - outerRadius + margin
      } else {
        x = centerX - innerRadius - (outerRadius - innerRadius) / 2 - margin
      }

      if (fillStyle) this.ctx.fillText(text, x, centerY + lineOffset)
      if (strokeStyle) this.ctx.strokeText(text, x, centerY + lineOffset)

      this.ctx.restore()
    } else if (orientation === 'vertical') {
      this._drawVerticalText(
        text,
        segment,
        alignment,
        centerX,
        centerY,
        outerRadius,
        innerRadius,
        fontSize,
        margin,
        fillStyle,
        strokeStyle,
        lineOffset,
        true
      )
    } else if (orientation === 'curved') {
      this._drawCurvedText(
        text,
        segment,
        alignment,
        centerX,
        centerY,
        outerRadius,
        innerRadius,
        fontSize,
        margin,
        fillStyle,
        strokeStyle,
        lineOffset,
        true
      )
    }
  }

  /**
   * Draw vertical text
   */
  _drawVerticalText(
    text,
    segment,
    alignment,
    centerX,
    centerY,
    outerRadius,
    innerRadius,
    fontSize,
    margin,
    fillStyle,
    strokeStyle,
    lineOffset,
    reversed
  ) {
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline =
      alignment === 'inner' ? (reversed ? 'top' : 'bottom') : alignment === 'outer' ? (reversed ? 'bottom' : 'top') : 'middle'

    const rotationAngle = segment.endAngle - (segment.endAngle - segment.startAngle) / 2 + (reversed ? -180 : 0)

    this.ctx.save()
    this.ctx.translate(centerX, centerY)
    this.ctx.rotate(this.degToRad(rotationAngle + this.rotationAngle))
    this.ctx.translate(-centerX, -centerY)

    let y
    if (alignment === 'outer') {
      y = reversed ? centerY + outerRadius - margin : centerY - outerRadius + margin
    } else if (alignment === 'inner') {
      y = reversed ? centerY + innerRadius + margin : centerY - innerRadius - margin
    } else {
      y = 0
    }

    const charHeight = fontSize - fontSize / 9

    if (alignment === 'outer') {
      for (let i = reversed ? text.length - 1 : 0; reversed ? i >= 0 : i < text.length; reversed ? i-- : i++) {
        const char = text.charAt(i)
        if (fillStyle) this.ctx.fillText(char, centerX + lineOffset, y)
        if (strokeStyle) this.ctx.strokeText(char, centerX + lineOffset, y)
        y += reversed ? -charHeight : charHeight
      }
    } else if (alignment === 'inner') {
      for (let i = reversed ? 0 : text.length - 1; reversed ? i < text.length : i >= 0; reversed ? i++ : i--) {
        const char = text.charAt(i)
        if (fillStyle) this.ctx.fillText(char, centerX + lineOffset, y)
        if (strokeStyle) this.ctx.strokeText(char, centerX + lineOffset, y)
        y += reversed ? charHeight : -charHeight
      }
    } else if (alignment === 'center') {
      let totalHeight = 0
      if (text.length > 1) {
        totalHeight = (charHeight * (text.length - 1)) / 2
      }

      let startY = centerY - innerRadius - (outerRadius - innerRadius) / 2 - totalHeight - margin

      for (let i = 0; i < text.length; i++) {
        const char = text.charAt(i)
        if (fillStyle) this.ctx.fillText(char, centerX + lineOffset, startY)
        if (strokeStyle) this.ctx.strokeText(char, centerX + lineOffset, startY)
        startY += charHeight
      }
    }

    this.ctx.restore()
  }

  /**
   * Draw curved text
   */
  _drawCurvedText(
    text,
    segment,
    alignment,
    centerX,
    centerY,
    outerRadius,
    innerRadius,
    fontSize,
    margin,
    fillStyle,
    strokeStyle,
    lineOffset,
    reversed
  ) {
    let radius
    if (alignment === 'inner') {
      radius = innerRadius + margin
      this.ctx.textBaseline = reversed ? 'top' : 'bottom'
    } else if (alignment === 'outer') {
      radius = outerRadius - margin
      this.ctx.textBaseline = reversed ? 'bottom' : 'top'
    } else {
      radius = innerRadius + margin + (outerRadius - innerRadius) / 2
      this.ctx.textBaseline = 'middle'
    }

    let charAngle = 0
    let startAngle

    if (text.length > 1) {
      this.ctx.textAlign = 'left'
      charAngle = (fontSize / 10) * 4
      const radiusRatio = 100 / radius
      charAngle *= radiusRatio
      const totalAngle = charAngle * text.length
      startAngle = segment.startAngle + ((segment.endAngle - segment.startAngle) / 2 - totalAngle / 2)
    } else {
      startAngle = segment.startAngle + (segment.endAngle - segment.startAngle) / 2
      this.ctx.textAlign = 'center'
    }

    startAngle += this.rotationAngle
    if (reversed) startAngle -= 180

    const iterStart = reversed ? text.length - 1 : 0
    const iterEnd = reversed ? -1 : text.length
    const iterStep = reversed ? -1 : 1

    for (let i = iterStart; reversed ? i > iterEnd : i < iterEnd; i += iterStep) {
      this.ctx.save()
      const char = text.charAt(i)

      this.ctx.translate(centerX, centerY)
      this.ctx.rotate(this.degToRad(startAngle))
      this.ctx.translate(-centerX, -centerY)

      const textY = reversed ? centerY + radius + lineOffset : centerY - radius + lineOffset

      if (strokeStyle) this.ctx.strokeText(char, centerX, textY)
      if (fillStyle) this.ctx.fillText(char, centerX, textY)

      startAngle += charAngle

      this.ctx.restore()
    }
  }

  /**
   * Convert degrees to radians
   */
  degToRad(degrees) {
    return degrees * (Math.PI / 180)
  }

  /**
   * Set center coordinates
   */
  setCenter(x, y) {
    this.centerX = x
    this.centerY = y
  }

  /**
   * Add a segment
   */
  addSegment(options, position) {
    const segment = new Segment(options)
    this.numSegments++

    if (position !== undefined) {
      for (let i = this.numSegments; i > position; i--) {
        this.segments[i] = this.segments[i - 1]
      }
      this.segments[position] = segment
    } else {
      this.segments[this.numSegments] = segment
    }

    this.updateSegmentSizes()
    return this.segments[position ?? this.numSegments]
  }

  /**
   * Set canvas ID
   */
  setCanvasId(id) {
    if (id) {
      this.canvasId = id
      this.canvas = document.getElementById(this.canvasId)
      if (this.canvas) {
        this.ctx = this.canvas.getContext('2d')
      }
    } else {
      this.canvasId = null
      this.ctx = null
      this.canvas = null
    }
  }

  /**
   * Delete a segment
   */
  deleteSegment(position) {
    if (this.numSegments <= 1) return

    if (position !== undefined) {
      for (let i = position; i < this.numSegments; i++) {
        this.segments[i] = this.segments[i + 1]
      }
    }

    this.segments[this.numSegments] = undefined
    this.numSegments--
    this.updateSegmentSizes()
  }

  /**
   * Convert window coordinates to canvas coordinates
   */
  windowToCanvas(x, y) {
    const rect = this.canvas.getBoundingClientRect()
    return {
      x: Math.floor(x - rect.left * (this.canvas.width / rect.width)),
      y: Math.floor(y - rect.top * (this.canvas.height / rect.height)),
    }
  }

  /**
   * Get segment at position
   */
  getSegmentAt(x, y) {
    const number = this.getSegmentNumberAt(x, y)
    return number !== null ? this.segments[number] : null
  }

  /**
   * Get segment number at position
   */
  getSegmentNumberAt(x, y) {
    const pos = this.windowToCanvas(x, y)
    const centerX = this.centerX * this.scaleFactor
    const centerY = this.centerY * this.scaleFactor
    const outerRadius = this.outerRadius * this.scaleFactor
    const innerRadius = this.innerRadius * this.scaleFactor

    let dx, dy, verticalDir, horizontalDir

    if (pos.x > centerX) {
      dx = pos.x - centerX
      horizontalDir = 'R'
    } else {
      dx = centerX - pos.x
      horizontalDir = 'L'
    }

    if (pos.y > centerY) {
      dy = pos.y - centerY
      verticalDir = 'B'
    } else {
      dy = centerY - pos.y
      verticalDir = 'T'
    }

    const ratio = dy / dx
    let angle = (Math.atan(ratio) * 180) / Math.PI
    let adjustedAngle = 0
    const distance = Math.sqrt(dy * dy + dx * dx)

    if (verticalDir === 'T' && horizontalDir === 'R') {
      adjustedAngle = Math.round(90 - angle)
    } else if (verticalDir === 'B' && horizontalDir === 'R') {
      adjustedAngle = Math.round(angle + 90)
    } else if (verticalDir === 'B' && horizontalDir === 'L') {
      adjustedAngle = Math.round(90 - angle + 180)
    } else if (verticalDir === 'T' && horizontalDir === 'L') {
      adjustedAngle = Math.round(angle + 270)
    }

    if (this.rotationAngle !== 0) {
      const rotationPos = this.getRotationPosition()
      adjustedAngle -= rotationPos
      if (adjustedAngle < 0) {
        adjustedAngle = 360 - Math.abs(adjustedAngle)
      }
    }

    for (let i = 1; i <= this.numSegments; i++) {
      if (
        adjustedAngle >= this.segments[i].startAngle &&
        adjustedAngle <= this.segments[i].endAngle &&
        distance >= innerRadius &&
        distance <= outerRadius
      ) {
        return i
      }
    }

    return null
  }

  /**
   * Get indicated segment
   */
  getIndicatedSegment() {
    const number = this.getIndicatedSegmentNumber()
    return this.segments[number]
  }

  /**
   * Get indicated segment number
   */
  getIndicatedSegmentNumber() {
    const rotationPos = this.getRotationPosition()
    let angle = Math.floor(this.pointerAngle - rotationPos)

    if (angle < 0) {
      angle = 360 - Math.abs(angle)
    }

    for (let i = 1; i < this.segments.length; i++) {
      if (angle >= this.segments[i].startAngle && angle <= this.segments[i].endAngle) {
        return i
      }
    }

    return 0
  }

  /**
   * Get current rotation position (normalized 0-360)
   */
  getRotationPosition() {
    let angle = this.rotationAngle

    if (angle >= 0) {
      if (angle > 360) {
        const fullRotations = Math.floor(angle / 360)
        angle -= 360 * fullRotations
      }
    } else {
      if (angle < -360) {
        const fullRotations = Math.ceil(angle / 360)
        angle -= 360 * fullRotations
      }
      angle = 360 + angle
    }

    return angle
  }

  /**
   * Start animation
   */
  startAnimation() {
    if (!this.animation) return

    this.computeAnimation()
    winwheelToDrawDuringAnimation = this

    const tweenConfig = {
      [this.animation.propertyName]: this.animation.propertyValue,
      yoyo: this.animation.yoyo,
      repeat: this.animation.repeat,
      ease: this.animation.easing,
      onUpdate: winwheelAnimationLoop,
      onComplete: winwheelStopAnimation,
    }

    // GSAP v3 syntax
    this.tween = gsap.to(this, {
      duration: this.animation.duration,
      ...tweenConfig,
    })
  }

  /**
   * Stop animation
   */
  stopAnimation(canCallback) {
    if (winwheelToDrawDuringAnimation) {
      if (winwheelToDrawDuringAnimation.tween) {
        winwheelToDrawDuringAnimation.tween.kill()
      }
      winwheelStopAnimation(canCallback)
    }
    winwheelToDrawDuringAnimation = this
  }

  /**
   * Pause animation
   */
  pauseAnimation() {
    if (this.tween) {
      this.tween.pause()
    }
  }

  /**
   * Resume animation
   */
  resumeAnimation() {
    if (this.tween) {
      this.tween.play()
    }
  }

  /**
   * Compute animation parameters
   */
  computeAnimation() {
    if (!this.animation) return

    if (this.animation.type === 'spinOngoing') {
      this.animation.propertyName = 'rotationAngle'
      this.animation.spins ??= 5
      this.animation.repeat ??= -1
      this.animation.easing ??= 'Linear.easeNone'
      this.animation.yoyo ??= false
      this.animation.propertyValue = 360 * this.animation.spins

      if (this.animation.direction === 'anti-clockwise') {
        this.animation.propertyValue = -this.animation.propertyValue
      }
    } else if (this.animation.type === 'spinToStop') {
      this.animation.propertyName = 'rotationAngle'
      this.animation.spins ??= 5
      this.animation.repeat ??= 0
      this.animation.easing ??= 'Power3.easeOut'
      this.animation.yoyo ??= false

      if (this.animation.stopAngle === null) {
        this.animation._stopAngle = Math.floor(Math.random() * 359)
      } else {
        this.animation._stopAngle = 360 - this.animation.stopAngle + this.pointerAngle
      }

      this.animation.propertyValue = 360 * this.animation.spins

      if (this.animation.direction === 'anti-clockwise') {
        this.animation.propertyValue = -this.animation.propertyValue
        this.animation.propertyValue -= 360 - this.animation._stopAngle
      } else {
        this.animation.propertyValue += this.animation._stopAngle
      }
    } else if (this.animation.type === 'spinAndBack') {
      this.animation.propertyName = 'rotationAngle'
      this.animation.spins ??= 5
      this.animation.repeat ??= 1
      this.animation.easing ??= 'Power2.easeInOut'
      this.animation.yoyo ??= true

      if (this.animation.stopAngle === null) {
        this.animation._stopAngle = 0
      } else {
        this.animation._stopAngle = 360 - this.animation.stopAngle
      }

      this.animation.propertyValue = 360 * this.animation.spins

      if (this.animation.direction === 'anti-clockwise') {
        this.animation.propertyValue = -this.animation.propertyValue
        this.animation.propertyValue -= 360 - this.animation._stopAngle
      } else {
        this.animation.propertyValue += this.animation._stopAngle
      }
    }
  }

  /**
   * Get random angle for segment
   */
  getRandomForSegment(segmentNumber) {
    if (!segmentNumber || !this.segments[segmentNumber]) return 0

    const start = this.segments[segmentNumber].startAngle
    const end = this.segments[segmentNumber].endAngle
    const range = end - start - 2

    if (range > 0) {
      return start + 1 + Math.floor(Math.random() * range)
    }

    return 0
  }
}

// Above this segment count, curved text is forced to horizontal for perf.
Winwheel.MAX_CURVED_TEXT_SEGMENTS = 150

export default Winwheel
